/*
 * Streak rules. Pure functions over day keys (yyyy-mm-dd, local time), so they are easy to test.
 *
 * - A streak is a run of days with at least one finished focus session.
 * - Saturday and Sunday are rest days: not working on them never breaks a streak (working on them still counts).
 *   Only a missed WEEKDAY breaks it.
 * - A lost streak can be won back, but this is deliberately never explained to the person (see the recovery
 *   offer in StreakRecoveryDialog): it has to feel like a surprise. The rules are:
 *   a session must be finished within RECOVERY_WEEKDAYS weekdays of the miss, and at most once per RECOVERY_COOLDOWN_DAYS.
 */

export const RECOVERY_WEEKDAYS = 3;
export const RECOVERY_COOLDOWN_DAYS = 15;
/** A streak shorter than this is not worth giving back. */
export const MIN_RECOVERABLE_STREAK = 3;

export interface StreakStats {
    lastDay: string;
    streak: number;
    /** Day of the last recovery, '' if never. */
    lastRecovery?: string;
}

const dayKeyOf = (d: Date): string =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

const parseKey = (key: string): Date => {
    const [y, m, d] = key.split('-').map(Number);
    return new Date(y, m - 1, d);
};

const addDays = (d: Date, n: number): Date =>
    new Date(d.getFullYear(), d.getMonth(), d.getDate() + n);

export const isRestDay = (d: Date): boolean =>
    d.getDay() === 0 || d.getDay() === 6;

/** Whole calendar days from `a` to `b` (negative when b is earlier). */
export const daysBetween = (a: string, b: string): number =>
    Math.round((parseKey(b).getTime() - parseKey(a).getTime()) / 86_400_000);

/** The weekdays strictly between two day keys, in order. */
const weekdaysBetween = (from: string, to: string): string[] => {
    const out: string[] = [];
    const end = parseKey(to);
    for (
        let d = addDays(parseKey(from), 1);
        d.getTime() < end.getTime();
        d = addDays(d, 1)
    ) {
        if (!isRestDay(d)) out.push(dayKeyOf(d));
    }
    return out;
};

/** Weekdays with no session between the last session day and `today` (neither included). */
export const missedWeekdays = (lastDay: string, today: string): number =>
    lastDay ? weekdaysBetween(lastDay, today).length : 0;

/** The streak as the person should see it right now: 0 once a weekday went by without a session. */
export const effectiveStreak = (stats: StreakStats, today: string): number =>
    stats.lastDay && missedWeekdays(stats.lastDay, today) === 0
        ? stats.streak
        : 0;

export type StreakStatus = 'none' | 'done' | 'rest' | 'open';

/**
 * - none: no streak running;
 * - done: a session was finished today;
 * - rest: a weekend with nothing to do, the streak is safe;
 * - open: a session is needed today.
 */
export const streakStatus = (stats: StreakStats, now: Date): StreakStatus => {
    const today = dayKeyOf(now);
    if (effectiveStreak(stats, today) === 0) return 'none';
    if (stats.lastDay === today) return 'done';
    return isRestDay(now) ? 'rest' : 'open';
};

/**
 * Called when a session is the first of its day and the streak has to restart. Returns true when the lost streak
 * may be offered back: long enough, close enough to the miss, and not too soon after the last recovery.
 */
export const canRecover = (stats: StreakStats, today: string): boolean => {
    if (!stats.lastDay || stats.streak < MIN_RECOVERABLE_STREAK) return false;
    const missed = weekdaysBetween(stats.lastDay, today);
    if (missed.length === 0) return false;
    // Weekdays after the first missed one, up to and including today.
    const elapsed = missed.length - 1 + (isRestDay(parseKey(today)) ? 0 : 1);
    if (elapsed > RECOVERY_WEEKDAYS) return false;
    if (
        stats.lastRecovery &&
        daysBetween(stats.lastRecovery, today) < RECOVERY_COOLDOWN_DAYS
    )
        return false;
    return true;
};

// ---- Reminders ----

export type ReminderKind = 'keep' | 'comeback' | 'congrats';

export interface Reminder {
    kind: ReminderKind;
    at: Date;
    /** The streak the message is about. */
    streak: number;
    /** Picks the wording, so the same day always reads the same. */
    variant: number;
}

export const REMINDER_HOUR = 18;
export const CONGRATS_HOUR = 9;
const DAYS_AHEAD = 14;
/** After this many missed weekdays the app stops nudging, rather than nagging. */
const MAX_COMEBACK_MISSES = 3;
export const MILESTONES = [3, 7, 14, 21, 30, 50, 75, 100, 150, 200, 365];

const dayOfYear = (d: Date): number =>
    Math.floor(
        (d.getTime() - new Date(d.getFullYear(), 0, 0).getTime()) / 86_400_000
    );

/**
 * What to send over the next two weeks, assuming no more sessions are finished (the plan is rebuilt after each one).
 * Never on weekends. Nothing for someone who has never finished a session.
 */
export const planReminders = (stats: StreakStats, now: Date): Reminder[] => {
    if (!stats.lastDay) return [];
    const out: Reminder[] = [];
    const todayKey = dayKeyOf(now);
    const doneToday = stats.lastDay === todayKey;
    let congratsPlaced = false;

    for (let i = 0; i <= DAYS_AHEAD; i++) {
        const day = addDays(new Date(now), i);
        if (isRestDay(day)) continue;
        const key = dayKeyOf(day);
        const variant = dayOfYear(day);

        if (doneToday && i > 0 && !congratsPlaced && stats.streak >= 2) {
            // The morning after a finished day: a pat on the back and a reason to open the app.
            congratsPlaced = true;
            out.push({
                kind: 'congrats',
                at: new Date(
                    day.getFullYear(),
                    day.getMonth(),
                    day.getDate(),
                    CONGRATS_HOUR
                ),
                streak: stats.streak,
                variant,
            });
        }

        if (key === todayKey && doneToday) continue;
        const missed = missedWeekdays(stats.lastDay, key);
        if (missed > MAX_COMEBACK_MISSES) break;
        const at = new Date(
            day.getFullYear(),
            day.getMonth(),
            day.getDate(),
            REMINDER_HOUR
        );
        if (at.getTime() <= now.getTime()) continue;
        out.push({
            kind: missed === 0 ? 'keep' : 'comeback',
            at,
            streak: stats.streak,
            variant,
        });
    }
    return out;
};
